import {useMutation} from '@apollo/client'
import React, {useEffect} from 'react'
import {Alert, Button, Modal, Spinner} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import {DELETE_COMMISSION_REPORT} from '../../../gql/mutations/commissionReportMutations'
import {toast} from 'react-toastify'
import {parseId} from '../../../helpers'
import { IResDeleteCommissionReport } from '../../../types/commissionReportApi'

interface IModalDeleteCommissionReport {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  infoEdit: {id: string; name: string}
}

export const ModalDeleteCommissionReport: React.FC<IModalDeleteCommissionReport> = ({
  infoEdit,
  isOpen,
  onClose,
  refetch,
}) => {
  const intl = useIntl()
  const [gqlDeleteCommissionReport, {data, loading, error}] = useMutation<IResDeleteCommissionReport>(DELETE_COMMISSION_REPORT)

  useEffect(() => {
    if (error) toast.error(intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message: error.message}))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  useEffect(() => {
    if (!data) return
    if (data.commissionReportDeleteMutation?.success) {
      toast.success(intl.formatMessage({id: 'commissions.reports.archive.toast.success', defaultMessage: 'Success: Commission report archived successfully'}))
      onClose()
      refetch()
    } else if (data.commissionReportDeleteMutation?.errors?.length > 0) {
      toast.error(intl.formatMessage(
        {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
        {message: data.commissionReportDeleteMutation?.errors?.[0]?.message || intl.formatMessage({id: 'commissions.logs.somethingHappened', defaultMessage: 'something happened'})}
      ))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const handleDelete = () => {
    gqlDeleteCommissionReport({
      variables: {
        id: parseId(infoEdit.id),
      },
    })
  }
  return (
    <Modal show={isOpen} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>
          <FormattedMessage id='commissions.reports.archive.title' defaultMessage='Archive Commission Report' />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <h4 className='text-primary'>
          <FormattedMessage
            id='commissions.reports.archive.confirm'
            defaultMessage='Are you sure you want to archive this commission report "{name}"?'
            values={{name: <span className='text-danger'>{infoEdit.name}</span>}}
          />
        </h4>
        <Alert
          variant='warning'
          className='mt-3 mb-0'
          style={{color: '#4a3b00', backgroundColor: '#fff8db', borderColor: '#f1c84c'}}
        >
          <FormattedMessage id='commissions.reports.archive.warning' defaultMessage='This action hides the report from the default list and prevents future edits. It does not permanently delete the record.' />
        </Alert>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
        </Button>
        <Button
          variant='warning'
          className='d-flex align-items-center'
          style={{color: '#1f1f1f', fontWeight: 600}}
          disabled={loading}
          onClick={handleDelete}
        >
          {loading && <Spinner animation='border' size='sm' />}
          <FormattedMessage id='commissions.reports.archive.button' defaultMessage='Archive' />
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
